import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import axios from "axios";
import {
  checkBoostEligibility,
  getAdAccounts,
  searchInterests,
  searchLocations,
  createBoost,
  getPostBoostHistory,
} from "../api/boost-api";
import { BoostConfig } from "../types";

// Query key factory
export const boostKeys = {
  all: ["boost"] as const,
  eligibility: (postId: string) =>
    [...boostKeys.all, "eligibility", postId] as const,
  adAccounts: () => [...boostKeys.all, "ad-accounts"] as const,
  interests: (query: string) => [...boostKeys.all, "interests", query] as const,
  locations: (query: string) => [...boostKeys.all, "locations", query] as const,
  history: (postId: string) => [...boostKeys.all, "history", postId] as const,
};

/**
 * Check whether a post is eligible for boosting.
 * Pass enabled=false to defer the query until the sheet is open.
 */
export function useBoostEligibility(postId: string, enabled = true) {
  return useQuery({
    queryKey: boostKeys.eligibility(postId),
    queryFn: () => checkBoostEligibility(postId),
    enabled: !!postId && enabled,
    retry: false,
  });
}

export function usePostBoostHistory(postId: string) {
  return useQuery({
    queryKey: boostKeys.history(postId),
    queryFn: () => getPostBoostHistory(postId),
    enabled: Boolean(postId),
    staleTime: 60 * 1000,
  });
}

/**
 * Fetch all available ad accounts
 */
export function useAdAccounts() {
  return useQuery({
    queryKey: boostKeys.adAccounts(),
    queryFn: getAdAccounts,
    enabled: true,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) =>
      axios.isAxiosError(error) && error.response?.status === 429
        ? false
        : failureCount < 2,
  });
}

/**
 * Search targeting interests with debounced query
 * Enabled when query is at least 2 characters
 */
export function useSearchInterests(query: string) {
  return useQuery({
    queryKey: boostKeys.interests(query),
    queryFn: () => searchInterests(query),
    enabled: query.length >= 2,
    placeholderData: keepPreviousData,
  });
}

/**
 * Search targeting locations with debounced query
 * Enabled when query is at least 2 characters
 */
export function useSearchLocations(query: string) {
  return useQuery({
    queryKey: boostKeys.locations(query),
    queryFn: () => searchLocations(query),
    enabled: query.length >= 2,
    placeholderData: keepPreviousData,
  });
}

/**
 * Create a boost for a post
 */
export function useCreateBoost(postId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (config: BoostConfig) => createBoost(postId, config),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: boostKeys.eligibility(postId),
      });
      queryClient.invalidateQueries({
        queryKey: boostKeys.history(postId),
      });
    },
  });
}
